/**
 * Global Privacy Control (GPC) Detection Module
 * 
 * This module provides functionality to detect if the user's browser has
 * Global Privacy Control (GPC) enabled. GPC is a privacy signal that indicates
 * the user's preference to opt-out of data sales and sharing.
 * 
 * Specification: https://globalprivacycontrol.org/
 */

/**
 * Detects if Global Privacy Control (GPC) is enabled in the user's browser.
 * 
 * GPC is a browser-level signal that indicates the user wants to opt-out of
 * data sales and sharing. When enabled, navigator.globalPrivacyControl === true.
 * 
 * This function is fail-safe: if any error occurs during detection (e.g., the
 * property doesn't exist or throws an exception), it returns false.
 * 
 * @returns {boolean} true if GPC is enabled, false otherwise
 */
export function isGpcEnabled(): boolean {
  try {
    // Check if navigator.globalPrivacyControl exists and is explicitly true
    // We use strict equality to ensure we only return true when GPC is definitively enabled
    if (typeof navigator !== 'undefined' && 'globalPrivacyControl' in navigator) {
      return (navigator as any).globalPrivacyControl === true;
    }
    
    return false;
  } catch (error) {
    // Fail-safe: if anything throws (e.g., accessing navigator in a restricted context),
    // we assume GPC is not enabled
    console.warn('Error detecting GPC:', error);
    return false;
  }
}
